import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Stack,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import WebIcon from '@mui/icons-material/Web';
import BuildIcon from '@mui/icons-material/Build';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { company } from '../company';

const services = [
  {
    title: 'Programmering',
    description:
      'Skreddersydde løsninger og automatisering — fra små verktøy til komplette systemer. Ryddig kode som er til å leve med.',
    icon: <CodeIcon sx={{ fontSize: 30 }} />,
  },
  {
    title: 'Web-design',
    description:
      'Moderne, responsive nettsider som ser bra ut på alle skjermer og laster raskt. Bygget for å bli funnet og brukt.',
    icon: <WebIcon sx={{ fontSize: 30 }} />,
  },
  {
    title: 'PC-reparasjon',
    description:
      'Feilsøking, oppgradering og vedlikehold av datamaskiner. Rask og ærlig hjelp når maskinen ikke gjør som den skal.',
    icon: <BuildIcon sx={{ fontSize: 30 }} />,
  },
];

const HomePage: React.FC = () => {
  const theme = useTheme();

  const facts = [
    { label: 'Organisasjonsnummer', value: company.orgNr },
    { label: 'Selskapsform', value: company.orgForm },
    { label: 'Registrert', value: company.registered },
    { label: 'Sted', value: `${company.city}, ${company.municipality}` },
  ];

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          background: `linear-gradient(160deg, #0b1f3a 0%, ${theme.palette.primary.dark} 55%, ${theme.palette.primary.main} 130%)`,
        }}
      >
        {/* subtle dotted grid */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
            maskImage: 'radial-gradient(ellipse at 30% 30%, #000 0%, transparent 75%)',
            WebkitMaskImage:
              'radial-gradient(ellipse at 30% 30%, #000 0%, transparent 75%)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 10, md: 16 } }}>
          <Box sx={{ maxWidth: 760 }}>
            <Chip
              icon={<VerifiedOutlinedIcon sx={{ color: 'inherit !important' }} />}
              label={`Registrert foretak · Org.nr ${company.orgNr}`}
              sx={{
                mb: 3,
                color: '#cfe3ff',
                bgcolor: alpha('#ffffff', 0.08),
                border: `1px solid ${alpha('#ffffff', 0.18)}`,
                fontWeight: 600,
                '& .MuiChip-icon': { color: '#cfe3ff' },
              }}
            />
            <Typography
              component="h1"
              variant="h2"
              sx={{ mb: 2, fontSize: { xs: '2.6rem', md: '4rem' }, lineHeight: 1.05 }}
            >
              Praktisk IT-hjelp
              <Box component="span" sx={{ display: 'block', color: theme.palette.secondary.light }}>
                fra Mosjøen.
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, color: alpha('#ffffff', 0.82), maxWidth: 560, mb: 5 }}
            >
              Bjørnå IT bygger nettsider, skriver programvare og fikser datamaskiner
              for privatpersoner og små bedrifter — uten dyre mellomledd.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                href={`mailto:${company.email}`}
                endIcon={<ArrowForwardIcon />}
                sx={{ borderRadius: 6, px: 4, py: 1.5, fontSize: '1.05rem' }}
              >
                Ta kontakt
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="#tjenester"
                sx={{
                  borderRadius: 6,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.05rem',
                  color: '#fff',
                  borderColor: alpha('#ffffff', 0.4),
                  '&:hover': { borderColor: '#fff', bgcolor: alpha('#ffffff', 0.06) },
                }}
              >
                Se tjenester
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Services */}
      <Container maxWidth="lg" id="tjenester" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: '80px' }}>
        <Typography variant="overline" color="primary" sx={{ display: 'block' }}>
          Tjenester
        </Typography>
        <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
          Dette kan jeg hjelpe med
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 620, mb: 6, fontSize: '1.1rem' }}>
          Tre kjerneområder — samme person fra første henvendelse til levert resultat.
        </Typography>

        <Grid container spacing={3}>
          {services.map((s) => (
            <Grid item xs={12} md={4} key={s.title}>
              <Box
                sx={{
                  height: '100%',
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 20px 40px rgba(15,30,51,0.10)',
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    mb: 2.5,
                  }}
                >
                  {s.icon}
                </Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  {s.title}
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {s.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* About + registry facts */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.035), py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" color="primary" sx={{ display: 'block' }}>
                Om foretaket
              </Typography>
              <Typography variant="h3" component="h2" sx={{ mb: 3 }}>
                Et registrert enkeltpersonforetak
              </Typography>
              <Typography sx={{ fontSize: '1.12rem', lineHeight: 1.8, color: 'text.secondary', mb: 2 }}>
                Bjørnå IT er et enkeltpersonforetak registrert i Foretaksregisteret,
                med base i {company.city}. Én kontaktperson gjennom hele oppdraget —
                ingen callsenter, ingen ventetid.
              </Typography>
              <Typography sx={{ fontSize: '1.12rem', lineHeight: 1.8, color: 'text.secondary', mb: 3 }}>
                Foretaket er oppført med næringskode {company.naeringskode.split(' – ')[0]} og
                driver med programmering, web-design og reparasjon av datamaskiner.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                <PlaceOutlinedIcon fontSize="small" />
                <Typography variant="body2">
                  {company.city}, {company.municipality} · Norge
                </Typography>
              </Stack>
              <Button
                href={company.brregUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ArrowForwardIcon />}
                sx={{ mt: 2, px: 0 }}
              >
                Se oppføring i Brønnøysundregistrene
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {facts.map((f) => (
                  <Grid item xs={12} sm={6} key={f.label}>
                    <Box
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}
                      >
                        {f.label}
                      </Typography>
                      <Typography variant="h6" sx={{ mt: 0.5 }}>
                        {f.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Typography variant="overline" color="primary" sx={{ display: 'block' }}>
          Kontakt
        </Typography>
        <Typography variant="h3" component="h2" sx={{ mb: 2 }}>
          La oss ta en prat
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '1.15rem', maxWidth: 560, mx: 'auto', mb: 4 }}>
          Har du et prosjekt, et problem eller bare et spørsmål? Send en e-post,
          så svarer jeg så fort jeg kan.
        </Typography>
        <Button
          variant="contained"
          size="large"
          href={`mailto:${company.email}`}
          sx={{ borderRadius: 6, px: 5, py: 1.5, fontSize: '1.1rem' }}
        >
          {company.email}
        </Button>
      </Container>
    </Box>
  );
};

export default HomePage;
